/**
 * 🔒 Recipient Certificates (S/MIME), a section of the Mail Settings window's
 * Administration group (piece 32; decision 1201; T2371): one `RecipientCertificate`
 * per recipient address, uploaded by an administrator or found by auto-discovery,
 * listed with its state, its source and its expiry. 🔨 Active, Expiring Soon and
 * Not Encrypted are derived from `notAfter` when the list is read and never stored;
 * a row holding a discovered certificate that differs reads Differs until reviewed.
 */
import { MAIL_API_PATH } from './client';
import { DAY_MS } from '../zonedTime';

/** The list and Upload; one row's Remove; one Differs row's Accept or Reject. */
export const MAIL_RECIPIENT_CERTIFICATES_PATH = `${MAIL_API_PATH}/settings/recipient-certificates`;
export const mailRecipientCertificatePath = (id: string): string => `${MAIL_RECIPIENT_CERTIFICATES_PATH}/${id}`;
export const mailRecipientCertificateReviewPath = (id: string): string => `${MAIL_RECIPIENT_CERTIFICATES_PATH}/${id}/review`;

/** 🔒 A certificate reads Expiring Soon from thirty days before its `notAfter` (T2371). */
export const CERTIFICATE_EXPIRING_SOON_DAYS = 30;

export const RECIPIENT_CERTIFICATE_STATES = ['Active', 'ExpiringSoon', 'NotEncrypted', 'Differs'] as const;
export type RecipientCertificateState = (typeof RECIPIENT_CERTIFICATE_STATES)[number];


/** The state a row reads at `now`: Differs while a discovered certificate waits, then by its `notAfter`. */
export function recipientCertificateState(row: { notAfter: Date | string; pendingCertificatePem: string | null }, now: Date): RecipientCertificateState {
  if (row.pendingCertificatePem) return 'Differs';
  const ends = new Date(row.notAfter).getTime();
  if (ends <= now.getTime()) return 'NotEncrypted';
  if (ends - now.getTime() <= CERTIFICATE_EXPIRING_SOON_DAYS * DAY_MS) return 'ExpiringSoon';
  return 'Active';
}

/** One row of the list, as the section draws it. */
export interface RecipientCertificateRow {
  id: string;
  version: number;
  recipientAddress: string;
  state: RecipientCertificateState;
  source: 'ManualUpload' | 'AutoDiscovered';
  fingerprint: string;
  notBefore: string;
  notAfter: string;
  /** The discovered certificate a Differs row waits on an administrator's review of; null on every other row. */
  pending: { fingerprint: string; notAfter: string; discoveredAt: string | null } | null;
}

export interface RecipientCertificatesAnswer {
  rows: RecipientCertificateRow[];
}

/** Upload: the address and the certificate's PEM text; a certificate already on file for the address is replaced. */
export interface RecipientCertificateUpload {
  recipientAddress: string;
  certificatePem: string;
}

/** Review on a Differs row: Accept installs the discovered certificate, Reject keeps the one on file. */
export interface RecipientCertificateReview {
  decision: 'Accept' | 'Reject';
  version?: number;
}

/** A certificate file's bytes as PEM text: a PEM file as it stands, a DER file wrapped as one. */
export function certificatePemOf(bytes: Uint8Array): string {
  const text = new TextDecoder().decode(bytes);
  if (text.includes('-----BEGIN CERTIFICATE-----')) return text.trim();
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const base64 = btoa(binary).replace(/(.{64})/g, '$1\n').trim();
  return `-----BEGIN CERTIFICATE-----\n${base64}\n-----END CERTIFICATE-----`;
}
